package com.castly.shared.dto

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement

/**
 * A single audit log entry returned by the API.
 *
 * RBAC masking rules applied at the service layer:
 *   ADMIN  → full data, actor identity visible
 *   EDITOR → actorId = "[hidden]", actorDisplayName = "[Hidden]"
 *   NONE   → own entries only (actor identity visible for own entries)
 */
@Serializable
data class AuditLogEntryDto(
    val id: String,
    val createdAt: String,          // ISO-8601
    val actorId: String,            // May be "[hidden]" per RBAC
    val actorDisplayName: String,   // May be "[Hidden]" per RBAC
    val actionType: String,         // Create | Update | Delete | Reorder | Publish | Unpublish | Import | BulkUpdate | Login | Logout
    val entityType: String,         // Podcast | Episode | Audiobook | ... | Session
    val entityId: String?,
    val entityName: String?,
    val changePayload: JsonElement?, // Varies by actionType; null for session events
    val sessionIp: String?,
    val labelName: String? = null
)

/**
 * Paginated response for the audit log list endpoint.
 */
@Serializable
data class AuditLogPageDto(
    val entries: List<AuditLogEntryDto>,
    val page: Int,
    val pageSize: Int,
    val totalCount: Long
)

/**
 * A distinct actor that appears in the audit log.
 * Used to populate the actor filter dropdown (ADMIN role only).
 */
@Serializable
data class AuditActorDto(
    val actorId: String,
    val actorDisplayName: String
)

/**
 * Filter parameters for querying the audit log.
 * All fields are optional — unset fields are not applied to the query.
 */
@Serializable
data class AuditLogFilterDto(
    val actorId: String? = null,        // Silently ignored for non-ADMIN callers
    val actionType: String? = null,
    val entityType: String? = null,
    val keyword: String? = null,        // ILIKE match on entity_name
    val labelName: String? = null,       // Filter by label name
    val dateFrom: String? = null,       // ISO-8601 date-time (inclusive)
    val dateTo: String? = null,         // ISO-8601 date-time (inclusive)
    val page: Int = 0,
    val pageSize: Int = 20
)
